import { forwardRef } from 'react';
import { BookOpen, CheckCircle, Clock, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate, formatDateOnly } from '@/lib/formatters';
import { isReadingOverdue } from '@/lib/reading-due';
import type { ReadingAssignment } from '@/types';

const CATEGORY_BADGES: Record<string, string> = {
  procedure: 'bg-blue-100 text-blue-700',
  policy: 'bg-green-100 text-green-700',
  form: 'bg-amber-100 text-amber-700',
  manual: 'bg-purple-100 text-purple-700',
  record: 'bg-gray-100 text-gray-600',
  other: 'bg-slate-100 text-slate-600',
};

type Props = {
  reading: ReadingAssignment;
  categoryLabel: (value: string) => string;
  highlighted: boolean;
  canConfirm: boolean;
  confirming: boolean;
  onOpen: () => void;
  onConfirm: () => void;
};

// Extraído de readings.tsx (ENG-LOC-01). #27: "Confirmar Lectura" queda
// deshabilitado hasta que el colaborador abra el documento (canConfirm).
export const ReadingCard = forwardRef<HTMLDivElement, Props>(function ReadingCard(
  { reading, categoryLabel, highlighted, canConfirm, confirming, onOpen, onConfirm },
  ref,
) {
  const pending = reading.confirmed_at === null;
  const overdue = isReadingOverdue(reading.due_date, pending);
  const canOpen = Boolean(reading.document.file_url || reading.document.external_url);

  return (
    <div
      ref={highlighted ? ref : undefined}
      data-testid={`reading-${reading.document_id}`}
      data-highlighted={highlighted}
      className={cn(
        'rounded-xl border bg-card p-5 transition-shadow hover:shadow-md',
        highlighted ? 'border-primary ring-2 ring-primary/40' : 'border-border',
      )}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <BookOpen
              className={cn(
                'mt-0.5 h-5 w-5 shrink-0',
                pending ? 'text-amber-500' : 'text-green-500',
              )}
            />
            <div className="min-w-0">
              <p className="font-mono text-xs font-bold text-muted-foreground">
                {reading.document.code}
              </p>
              <h3 className="mt-0.5 text-sm font-semibold text-card-foreground">
                {reading.document.title}
              </h3>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    CATEGORY_BADGES[reading.document.category] ?? CATEGORY_BADGES.other,
                  )}
                >
                  {categoryLabel(reading.document.category)}
                </span>
                {pending ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                    <Clock className="h-3 w-3" />
                    Pendiente
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                    <CheckCircle className="h-3 w-3" />
                    Confirmada
                  </span>
                )}
                {overdue && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                    <Clock className="h-3 w-3" />
                    Vencida
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span>Asignada: {formatDate(reading.assigned_at)}</span>
            {reading.due_date && (
              <span className={cn(overdue && 'font-medium text-red-700')}>
                Fecha límite: {formatDateOnly(reading.due_date)}
              </span>
            )}
            {reading.confirmed_at && <span>Confirmada: {formatDate(reading.confirmed_at)}</span>}
            {reading.version_number && <span>Versión: {reading.revision_label}</span>}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            {canOpen && (
              <button
                type="button"
                onClick={onOpen}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium text-card-foreground transition-colors hover:bg-accent"
              >
                <Eye className="h-3.5 w-3.5" />
                Ver Documento
              </button>
            )}
            {pending && (
              <button
                type="button"
                onClick={onConfirm}
                disabled={confirming || !canConfirm}
                title={!canConfirm ? 'Abre el documento antes de confirmar la lectura' : undefined}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90',
                  (confirming || !canConfirm) && 'cursor-not-allowed opacity-60',
                )}
              >
                <CheckCircle className="h-3.5 w-3.5" />
                {confirming ? 'Confirmando...' : 'Confirmar Lectura'}
              </button>
            )}
          </div>
          {pending && !canConfirm && (
            <p className="text-[11px] text-muted-foreground">Abre el documento para confirmar</p>
          )}
        </div>
      </div>
    </div>
  );
});
